/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, type FC } from 'react';
import { css, jsx } from '@compiled/react';
import { getCloudId } from '@helpcenter/meta';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import { auth } from '@atlaskit/outbound-auth-flow-client';
import { token } from '@atlaskit/tokens';
import { fireOperationalAnalytics, fireUIAnalytics } from '@atlassian/analytics-bridge';
import { IntegrationProviderMeta } from '../../../common/constants';
import { useConnectionStatus } from '../../../controllers/store';
import messages from './messages';
import type { CardConnectorsProps } from './types';

export const ThirdPartyCardConnector: FC<CardConnectorsProps> = ({ provider }) => {
    di(useConnectionStatus, getCloudId, auth);
    const [{ data: connectionStatus }, { updateConnectionStatus }] = useConnectionStatus();
    const { formatMessage } = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const currentProviderStatus = connectionStatus[provider];

    const handleAuth = useCallback(
        (_, analyticsEvent: UIAnalyticsEvent) => {
            const href = currentProviderStatus?.outboundAuthUrl;
            if (!href) return;
            auth(href).then(
                () => {
                    updateConnectionStatus(provider);
                    fireOperationalAnalytics(analyticsEvent, '3pAuthConnection succeeded', { provider });
                },
                (err: Error) => {
                    const error = err ? err : new Error('Unknown Error Failed to connect');
                    fireOperationalAnalytics(analyticsEvent, '3pAuthConnection failed', {
                        provider,
                        error: error?.message,
                    });
                }
            );
            fireOperationalAnalytics(analyticsEvent, 'cardConnectAuth clicked', {
                provider,
            });
        },
        [currentProviderStatus?.outboundAuthUrl, provider, updateConnectionStatus]
    );

    if (!connectionStatus[provider] || connectionStatus[provider]?.isUserOAuthed) return null;
    fireUIAnalytics(createAnalyticsEvent({}), 'cardThirdPartyConnector viewed', { provider });
    const Icon = IntegrationProviderMeta[provider].icon;
    return (
        <div css={containerStyles} role="status" aria-live="polite">
            <div css={innerContentStyles}>
                <Icon label="" />
                <div css={stackStyles}>
                    <Heading as="h2" level="h400">
                        {formatMessage(messages.cardConnectorTitle, {
                            provider: IntegrationProviderMeta[provider].name,
                        })}
                    </Heading>
                    <div css={textStyles}>{formatMessage(messages.cardConnectorDescription)}</div>
                </div>
            </div>

            <Button appearance="primary" onClick={handleAuth}>
                {formatMessage(messages.connectButton)}
            </Button>
        </div>
    );
};

const containerStyles = css({
    padding: token('space.250', '1.25rem'),
    backgroundColor: token('color.background.accent.blue.subtlest', '#E9F2FF'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: token('space.100', '0.5rem'),
    borderRadius: token('border.radius.100', '3px'),
    margin: token('space.100', '0.5rem'),
});

const innerContentStyles = css({
    display: 'flex',
    alignItems: 'center',
    gap: token('space.200', '1rem'),
});

const stackStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: token('space.100', '0.5rem'),
});

const textStyles = css({
    fontSize: '14px',
    lineHeight: '20px',
    color: token('color.text', '#172B4D'),
});
